import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Avatar, Button, Card, Chip, EmptyState, Icon, Skeleton, Tabs, fmtNum } from "../components/ui";
import CharacterCard, { Cover } from "../components/character/Card";
import Comments from "../components/social/Comments";
import ReportModal from "../components/social/ReportModal";
import useMe from "../components/social/useMe";
import { copyText } from "../components/character/ShareModal";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../services/api";
import content from "../services/api-content";
import "../styles/content.css";

export default function Universe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { profile, isSuperUser } = useAuth();
  const me = useMe();
  const [tab, setTab] = useState("info");
  const [liked, setLiked] = useState(null);
  const [likes, setLikes] = useState(null);
  const [report, setReport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cc, setCc] = useState(null);
  const { data: u, loading, error } = useAsync(() => content.universes.get(id), [id]);

  if (error) return <EmptyState icon="🌌" title="ไม่พบจักรวาลนี้" action={<Button onClick={() => navigate("/?tab=universes")}>กลับ</Button>}>{error}</EmptyState>;
  if (loading) return <div className="stack"><Skeleton height={220} radius={24} /><Skeleton height={24} width="50%" /></div>;

  const chars = u.characters || [];
  const isLiked = liked ?? !!u.isLiked;
  const likeCount = likes ?? (Number(u.likeCount) || 0);
  const toggleLike = async () => {
    try { const r = await content.like("universe", id); setLiked(r.liked); setLikes(r.likeCount ?? likeCount + (r.liked ? 1 : -1)); } catch (e) { toast(e.message, "error"); }
  };
  const share = async () => toast((await copyText(`${window.location.origin}/universe/${id}`)) ? "คัดลอกลิงก์จักรวาลแล้ว" : "คัดลอกไม่สำเร็จ", "success");
  const start = async () => {
    if (!chars.length) return toast("จักรวาลนี้ยังไม่มีตัวละคร", "error");
    setBusy(true);
    try {
      const chat = await content.universes.startChat(id, chars[0].id, (cid) => api.chats.create(cid));
      navigate(`/chat/${chat.id}`);
    } catch (e) { toast(e.message, "error"); setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div className="row mb-16">
        <Button size="sm" icon variant="ghost" aria-label="ย้อนกลับ" onClick={() => navigate(-1)}><Icon name="back" /></Button>
        <div className="grow" />
        <Button size="sm" icon variant="ghost" aria-label="แชร์" onClick={share}><Icon name="send" /></Button>
        <Button size="sm" icon variant="ghost" aria-label={isLiked ? "เลิกถูกใจ" : "ถูกใจ"} aria-pressed={isLiked} onClick={toggleLike} style={isLiked ? { color: "var(--accent)" } : undefined}><Icon name="heart" filled={isLiked} /></Button>
        {!u.isOwner ? <Button size="sm" icon variant="ghost" aria-label="รายงาน" onClick={() => setReport(true)}><Icon name="shield" /></Button> : null}
      </div>
      <Cover c={{ ...u, emoji: u.emoji || "🌍" }} size="med" style={{ aspectRatio: "16 / 8", fontSize: 80, width: "100%" }} />
      <div className="mt-16">
        <h1>{u.name}</h1>
        {u.tagline ? <p className="muted mt-8">{u.tagline}</p> : null}
        {u.creator?.id ? <Link to={`/creator/${u.creator.id}`} className="row mt-8" style={{ gap: 8 }}><Avatar name={u.creator.displayName} size={26} />โดย @{u.creator.username || u.creator.displayName}</Link> : null}
        {(u.tags || []).length ? <div className="chip-row mt-16" style={{ flexWrap: "wrap" }}>{u.tags.map((t) => <Chip key={t} size="sm">{t}</Chip>)}</div> : null}
        <div className="stats card mt-16">
          <div className="stat"><b>{fmtNum(likeCount)}</b><span>ไลก์</span></div>
          <div className="stat"><b>{fmtNum(u.messageCount)}</b><span>ข้อความ</span></div>
          <div className="stat"><b>{fmtNum(u.chatCount)}</b><span>แชท</span></div>
          <div className="stat"><b>{chars.length}</b><span>ตัวละคร</span></div>
        </div>
        <Button variant="primary" size="lg" block className="mt-16" onClick={start} disabled={busy}>{busy ? "กำลังเปิดห้องแชท..." : "เริ่มแชทในจักรวาล"}</Button>
        {u.isOwner ? <Link className="btn btn--block mt-8" to={`/create?type=universe&edit=${u.id}`}>แก้ไขจักรวาล</Link> : null}
      </div>
      <div className="mt-24">
        <Tabs variant="line" value={tab} onChange={setTab} tabs={[{ value: "info", label: "รายละเอียด" }, { value: "chars", label: `ตัวละคร (${chars.length})` }, { value: "comments", label: `ความคิดเห็น${cc !== null ? ` (${cc})` : ""}` }]} />
        <div className="mt-16">
          {tab === "info" ? (
            <Card className="stack">
              {u.history ? <div><div className="strong">ประวัติจักรวาล</div><p className="muted mt-8" style={{ whiteSpace: "pre-wrap" }}>{u.history}</p></div> : null}
              {u.greeting ? <div><div className="strong">คำทักทาย</div><p className="muted mt-8" style={{ whiteSpace: "pre-wrap" }}>{u.greeting.replaceAll("{{user}}", profile?.displayName || "คุณ")}</p></div> : null}
              <div className="muted" style={{ fontSize: 13 }}>โหมดความจำ: {u.memoryMode === "extended40" ? "ขยาย (40 ข้อความ)" : "มาตรฐาน (20 ข้อความ)"}</div>
              {!u.history && !u.greeting ? <span className="faint">ผู้สร้างยังไม่ได้เพิ่มรายละเอียด</span> : null}
            </Card>
          ) : tab === "chars" ? (
            chars.length ? <div className="grid-cards">{chars.map((c) => <CharacterCard key={c.id} c={c} userName={profile?.displayName} />)}</div> : <EmptyState icon="👥" title="ยังไม่มีตัวละครในจักรวาลนี้" />
          ) : <Card><Comments targetType="universe" targetId={id} ownerId={u.createdBy} meId={me?.id} isSuper={isSuperUser} onCount={setCc} /></Card>}
        </div>
      </div>
      <ReportModal open={report} onClose={() => setReport(false)} targetType="universe" targetId={id} title="รายงานจักรวาล" />
    </div>
  );
}
